import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Automatic logout after this many milliseconds of inactivity (mouse/keyboard/touch/API).
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_WARN_MS = 60 * 1000; // warn 60s before logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hsg_user") || "null"); } catch { return null; }
  });
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hsg_admin_meta") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("hsg_user_token", data.access_token);
    localStorage.setItem("hsg_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("hsg_user_token", data.access_token);
    localStorage.setItem("hsg_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("hsg_user_token");
    localStorage.removeItem("hsg_user");
    setUser(null);
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    const { data } = await api.post("/admin/login", { email, password });
    localStorage.setItem("hsg_admin_token", data.access_token);
    const meta = {
      name: data.name,
      email,
      allowedAteliers: data.allowedAteliers || [],
      allowedMenus: data.allowedMenus || [],
      allowedContentKinds: data.allowedContentKinds || [],
      role: data.role || (data.isSuperAdmin ? "super" : "custom"),
      isSuperAdmin: !!data.isSuperAdmin,
    };
    localStorage.setItem("hsg_admin_meta", JSON.stringify(meta));
    setAdmin(meta);
    return meta;
  }, []);

  const adminLogout = useCallback(() => {
    localStorage.removeItem("hsg_admin_token");
    localStorage.removeItem("hsg_admin_meta");
    setAdmin(null);
  }, []);

  // --- Auto-logout after inactivity (only when admin is logged in) ---
  const resetIdleTimer = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (!localStorage.getItem("hsg_admin_token")) return; // only when logged in
    warnTimerRef.current = setTimeout(() => {
      toast.warning("Vous serez déconnecté dans 1 minute par inactivité.", { duration: 8000 });
    }, IDLE_TIMEOUT_MS - IDLE_WARN_MS);
    idleTimerRef.current = setTimeout(() => {
      adminLogout();
      // Redirect to login page (root)
      try {
        toast.error("Déconnecté automatiquement pour inactivité.");
        if (window.location.pathname !== "/") window.location.assign("/");
      } catch (e) { /* ignore */ }
    }, IDLE_TIMEOUT_MS);
  }, [adminLogout]);

  useEffect(() => {
    if (!admin) {
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }
    // Listen to activity events
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel", "visibilitychange"];
    const handler = () => {
      if (document.visibilityState === "hidden") return; // ignore when tab hidden
      resetIdleTimer();
    };
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [admin, resetIdleTimer]);

  useEffect(() => {
    // Optional: refresh user profile on load
    const token = localStorage.getItem("hsg_user_token");
    if (token && !user) {
      setLoading(true);
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        localStorage.setItem("hsg_user", JSON.stringify(data));
      }).catch(() => {
        localStorage.removeItem("hsg_user_token");
      }).finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, admin, loading, login, register, logout, adminLogin, adminLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
